import {
    collection,
    addDoc,
    updateDoc,
    doc,
    getDocs,
    getDoc,
    deleteDoc,
    arrayUnion,
    arrayRemove,
    query,
    where,
    documentId,
    deleteField,
    runTransaction,
    orderBy,
    limit,
    startAfter,
    endBefore,
    limitToLast,
    collectionGroup,
    setDoc,
} from 'firebase/firestore';
import moment from "moment-timezone";
import { reportFirestoreReadError, reportFirestoreWriteError } from "./firestoreErrorUtils";
import { auth, db } from "../firebase";
import { isListEmpty, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "./objectUtils";

const readQuery = async (query) => {
    const snapshot = await getDocs(query);
    // doc.data() is never undefined for query doc snapshots
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id, // append document id to each document
            ...doc.data()
        };
    });
};

export const getByQuery = async (query, collectionName = 'query') => {
    try {
        return await readQuery(query);
    } catch (error) {
        reportFirestoreReadError('getByQuery', collectionName, error);
        throw error;
    }
};

export const getAllPaginated = async (collectionName, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
    let q = query(collection(db, collectionName), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
    if (isStringNotEmpty(firstVisible)) {
        q = query(collection(db, collectionName), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
    } else if (isStringNotEmpty(lastVisible)) {
        q = query(collection(db, collectionName), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id, // append document id to each document
            ...doc.data()
        };
    });
};

export const getByQueryPaginated = async (collectionName, fieldName, fieldValue, comparer, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
    let q = query(collection(db, collectionName), where(fieldName, comparer, fieldValue), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
    if (isStringNotEmpty(firstVisible)) {
        q = query(collection(db, collectionName), where(fieldName, comparer, fieldValue), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
    } else if (isStringNotEmpty(lastVisible)) {
        q = query(collection(db, collectionName), where(fieldName, comparer, fieldValue), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id, // append document id to each document
            ...doc.data()
        };
    });
};

export const getByDoubleQueryPaginated = async (collectionName, fieldName, fieldValue, comparer, fieldName2, fieldValue2, comparer2, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
    let q = query(collection(db, collectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
    if (isStringNotEmpty(firstVisible)) {
        q = query(collection(db, collectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
    } else if (isStringNotEmpty(lastVisible)) {
        q = query(collection(db, collectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2), orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id, // append document id to each document
            ...doc.data()
        };
    });
};

export const getBySingleQuery = async (collectionName, fieldName, fieldValue, comparer) => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListNotEmpty(fieldValue) && fieldValue.length > 10) {
        return await getByBatchQuery(collectionName, fieldName, fieldValue, comparer);
    } else {
        return await getByQuery(query(collection(db, collectionName), where(fieldName, comparer, fieldValue)), collectionName);
    }
};

export const getByDoubleQuery = async (collectionName, fieldName, fieldValue, comparer, fieldName2, fieldValue2, comparer2) => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }
    if ((comparer2 === '==' || comparer2 === 'array-contains') && isStringEmpty(fieldValue2)) {
        return [];
    }
    if ((comparer2 === 'in' || comparer2 === 'not-in' || comparer2 === 'array-contains-any') && isListEmpty(fieldValue2)) {
        return [];
    }
    return await getByQuery(query(collection(db, collectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2)), collectionName);
};

export const getByDoubleQueryWithList = async (collectionName, field1Name, field1Value, comparer1, field2Name, field2List = [], comparer2) => {
    if ((comparer1 === '==' || comparer1 === 'array-contains') && isStringEmpty(field1Value)) {
        return [];
    }
    if ((comparer2 === 'in' || comparer2 === 'not-in' || comparer2 === 'array-contains-any') && isListEmpty(field2List)) {
        return [];
    }

    if ((comparer2 === 'in' || comparer2 === 'not-in' || comparer2 === 'array-contains-any') && isListNotEmpty(field2List) && field2List.length > 10) {
        return await getByBatchDoubleQuery(collectionName, field1Name, field1Value, comparer1, field2Name, field2List, comparer2);
    } else {
        return await getByQuery(query(collection(db, collectionName), where(field1Name, comparer1, field1Value), where(field2Name, comparer2, field2List)), collectionName);
    }
};

export const getByTripleQuery = async (collectionName, fieldName, fieldValue, comparer, fieldName2, fieldValue2, comparer2, fieldName3, fieldValue3, comparer3) => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }
    if ((comparer2 === '==' || comparer2 === 'array-contains') && isStringEmpty(fieldValue2)) {
        return [];
    }
    if ((comparer2 === 'in' || comparer2 === 'not-in' || comparer2 === 'array-contains-any') && isListEmpty(fieldValue2)) {
        return [];
    }
    if ((comparer3 === '==' || comparer3 === 'array-contains') && isStringEmpty(fieldValue3)) {
        return [];
    }
    if ((comparer3 === 'in' || comparer3 === 'not-in' || comparer3 === 'array-contains-any') && isListEmpty(fieldValue3)) {
        return [];
    }
    return await getByQuery(query(collection(db, collectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2), where(fieldName3, comparer3, fieldValue3)), collectionName);
};

export const getByIdQuery = async (collectionName, fieldValue, comparer) => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListNotEmpty(fieldValue) && fieldValue.length > 10) {
        return await getByIdBatchQuery(collectionName, fieldValue, comparer);
    } else {
        return await getByQuery(query(collection(db, collectionName), where(documentId(), comparer, fieldValue)), collectionName);
    }
};

export const getByIdDoubleQuery = async (collectionName, fieldValue, comparer, fieldName2, fieldValue2, comparer2) => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListNotEmpty(fieldValue) && fieldValue.length > 10) {
        return await getByIdBatchDoubleQuery(collectionName, fieldValue, comparer, fieldName2, fieldValue2, comparer2);
    } else {
        return await getByQuery(query(collection(db, collectionName), where(fieldName2, comparer2, fieldValue2), where(documentId(), comparer, fieldValue)), collectionName);
    }
};

export function getByIdBatchQuery(collectionName, ids, comparer) {
    return new Promise((res, rej) => {
        // don't run if there aren't any ids
        if (!ids || !ids.length) return res([]);

        let batches = [];

        // batches of 10 stay under firestore's in-query limit; slicing leaves the caller's list as it was
        for (let index = 0; index < ids.length; index += 10) {
            const batch = ids.slice(index, index + 10);

            batches.push(readQuery(query(collection(db, collectionName), where(documentId(), comparer, batch))));
        }

        // a failed batch rejects the read, reported once for the read rather than once per batch
        Promise.all(batches).then(content => {
            res(content.flat());
        }, (error) => {
            reportFirestoreReadError('getByIdBatchQuery', collectionName, error);
            rej(error);
        });
    });
};

export function getByIdBatchDoubleQuery(collectionName, ids, comparer, fieldName2, fieldValue2, comparer2) {
    return new Promise((res, rej) => {
        // don't run if there aren't any ids
        if (!ids || !ids.length) return res([]);

        let batches = [];

        // batches of 10 stay under firestore's in-query limit; slicing leaves the caller's list as it was
        for (let index = 0; index < ids.length; index += 10) {
            const batch = ids.slice(index, index + 10);

            batches.push(readQuery(query(collection(db, collectionName), where(fieldName2, comparer2, fieldValue2), where(documentId(), comparer, batch))));
        }

        // a failed batch rejects the read, reported once for the read rather than once per batch
        Promise.all(batches).then(content => {
            res(content.flat());
        }, (error) => {
            reportFirestoreReadError('getByIdBatchDoubleQuery', collectionName, error);
            rej(error);
        });
    });
};

export function getByBatchQuery(collectionName, fieldName, fieldValue, comparer) {
    return new Promise((res, rej) => {
        // don't run if there aren't any ids
        if (!fieldValue || !fieldValue.length) return res([]);

        let batches = [];

        // batches of 10 stay under firestore's in-query limit; slicing leaves the caller's list as it was
        for (let index = 0; index < fieldValue.length; index += 10) {
            const batch = fieldValue.slice(index, index + 10);

            batches.push(readQuery(query(collection(db, collectionName), where(fieldName, comparer, batch))));
        }

        // a failed batch rejects the read, reported once for the read rather than once per batch
        Promise.all(batches).then(content => {
            res(content.flat());
        }, (error) => {
            reportFirestoreReadError('getByBatchQuery', collectionName, error);
            rej(error);
        });
    });
};

export function getByBatchDoubleQuery(collectionName, field1Name, field1Value, comparer1, field2Name, field2List, comparer2) {
    return new Promise((res, rej) => {
        // don't run if there aren't any ids
        if (!field2List || !field2List.length) return res([]);

        let batches = [];

        // batches of 10 stay under firestore's in-query limit; slicing leaves the caller's list as it was
        for (let index = 0; index < field2List.length; index += 10) {
            const batch = field2List.slice(index, index + 10);

            batches.push(readQuery(query(collection(db, collectionName), where(field1Name, comparer1, field1Value), where(field2Name, comparer2, batch))));
        }

        // a failed batch rejects the read, reported once for the read rather than once per batch
        Promise.all(batches).then(content => {
            res(content.flat());
        }, (error) => {
            reportFirestoreReadError('getByBatchDoubleQuery', collectionName, error);
            rej(error);
        });
    });
};

export function getByBatchTripleQuery(collectionName, field1Name, field1Value, comparer1, field2Name, field2Value, comparer2, field3Name, field3List, comparer3) {
    return new Promise((res, rej) => {
        // don't run if there aren't any ids
        if (!field3List || !field3List.length) return res([]);

        let batches = [];

        // batches of 10 stay under firestore's in-query limit; slicing leaves the caller's list as it was
        for (let index = 0; index < field3List.length; index += 10) {
            const batch = field3List.slice(index, index + 10);

            batches.push(readQuery(query(collection(db, collectionName), where(field1Name, comparer1, field1Value), where(field2Name, comparer2, field2Value), where(field3Name, comparer3, batch))));
        }

        // a failed batch rejects the read, reported once for the read rather than once per batch
        Promise.all(batches).then(content => {
            res(content.flat());
        }, (error) => {
            reportFirestoreReadError('getByBatchTripleQuery', collectionName, error);
            rej(error);
        });
    });
};

export const getAll = async (collectionName) => {
    const q = query(collection(db, collectionName));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id, // append document id to each document
            ...doc.data()
        };
    });
};

const readReference = async (ref) => {
    const docSnap = await getDoc(ref);

    const data = docSnap.exists() ? docSnap.data() : null;

    if (data === null || data === undefined) return null;

    return { id: ref.id, ...data };
};

// resolve a relation, returns the referenced document
export const getReference = async (ref) => {
    try {
        return await readReference(ref);
    } catch (error) {
        reportFirestoreReadError('getReference', ref?.path, error);
        throw error;
    }
};

// returns a single document in object format
export const getById = async (collectionName, id) => {
    if (!id) return null; // entity form is in create mode
    const ref = doc(db, collectionName, id);

    return await getReference(ref);
};

// the base fields every helper below stamps, for a write made inside a caller's own batch or transaction
export const getCreateFields = (at = moment().utc().toDate()) => {
    const by = isNotNullOrUndefined(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
    return { createdAt: at, createdBy: by, modifiedAt: at, modifiedBy: by, isDeleted: false };
};

export const getUpdateFields = (at = moment().utc().toDate()) => {
    return { modifiedAt: at, modifiedBy: isNotNullOrUndefined(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' };
};

// save a new document in the database
export const create = async (collectionName, data) => {
    try {
        data.createdAt = moment().utc().toDate();
        data.createdBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.modifiedAt = moment().utc().toDate();
        data.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.isDeleted = false;

        const ref = await addDoc(collection(db, collectionName), data);
        try {
            return await readReference(ref);
        } catch (error) {
            // the document is written; a collection the rules let a user add to but not read back must not have its
            // writes answered as failures, or reported as denied
            return { ...data, id: ref.id };
        }
    } catch (error) {
        reportFirestoreWriteError('create', collectionName, error);
    }

    return null;
};

// save a new document in the database
export const createBlind = async (collectionName, data) => {
    try {
        data.createdAt = moment().utc().toDate();
        data.createdBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.modifiedAt = moment().utc().toDate();
        data.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.isDeleted = false;

        const ref = await addDoc(collection(db, collectionName), data);
        if (isStringNotEmpty(ref.id)) {
            return { ...data, id: ref.id };
        }
    } catch (error) {
        reportFirestoreWriteError('createBlind', collectionName, error);
    }

    return null;
};

export const createWithId = async (collectionName, id, data) => {
    try {
        data.createdAt = moment().utc().toDate();
        data.createdBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.modifiedAt = moment().utc().toDate();
        data.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.isDeleted = false;

        const ref = doc(db, collectionName, id);

        await runTransaction(db, async (transaction) => {
            const docSnap = await transaction.get(ref);
            // if the doc doesn't exist then we are good to create it
            if (!docSnap.exists()) {
                transaction.set(ref, data);
            } else {
                const docData = docSnap.data();

                if (docData === null || docData === undefined) {
                    transaction.set(ref, data);
                }
            }
        });

        return await getReference(ref);
    } catch (error) {
        reportFirestoreWriteError('createWithId', `${collectionName}/${id}`, error);
    }

    return null;
};

export const createBlindWithId = async (collectionName, id, data) => {
    try {
        data.createdAt = moment().utc().toDate();
        data.createdBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.modifiedAt = moment().utc().toDate();
        data.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.isDeleted = false;

        await setDoc(doc(db, collectionName, id), data);

        return { ...data, id: id };
    } catch (error) {
        reportFirestoreWriteError('createBlindWithId', `${collectionName}/${id}`, error);
    }

    return null;
};

// update an existing document with new data
export const update = async (collectionName, id, data) => {
    try {
        data.modifiedAt = moment().utc().toDate();
        data.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';

        const ref = doc(db, collectionName, id);
        await updateDoc(ref, data);

        return true;
    } catch (error) {
        reportFirestoreWriteError('update', `${collectionName}/${id}`, error);
    }

    return false;
};

// update an existing document with new field data
export const updateField = async (collectionName, id, fieldName, newValue) => {
    try {
        const ref = doc(db, collectionName, id);
        await updateDoc(ref, { [fieldName]: newValue, modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

        return true;
    } catch (error) {
        reportFirestoreWriteError('updateField', `${collectionName}/${id}`, error);
    }

    return false;
};

export const updateFields = async (collectionName, id, updatedFields) => {
    try {
        const ref = doc(db, collectionName, id);
        await updateDoc(ref, { ...updatedFields, modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

        return true;
    } catch (error) {
        reportFirestoreWriteError('updateFields', `${collectionName}/${id}`, error);
    }

    return false;
};

export const removeField = async (collectionName, id, fieldName) => {
    try {
        const ref = doc(db, collectionName, id);
        await updateDoc(ref, { [fieldName]: deleteField() });

        return true;
    } catch (error) {
        reportFirestoreWriteError('removeField', `${collectionName}/${id}`, error);
    }

    return false;
};

// delete an existing document from the collection
export const remove = async (collectionName, id) => {
    try {
        const ref = doc(db, collectionName, id);
        await deleteDoc(ref);

        return true;
    } catch (error) {
        reportFirestoreWriteError('remove', `${collectionName}/${id}`, error);
    }

    return false;
};

// add a value to a child array within an existing document
export const addValueToChildArray = async (collectionName, id, childArrayFieldName, newValue) => {
    try {
        const ref = doc(db, collectionName, id);
        await updateDoc(ref, { [childArrayFieldName]: arrayUnion(newValue), modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

        return true;
    } catch (error) {
        reportFirestoreWriteError('addValueToChildArray', `${collectionName}/${id}`, error);
    }

    return false;
};

// remove a value from a child array within an existing document
export const removeValueFromChildArray = async (collectionName, id, childArrayFieldName, removeValue) => {
    try {
        const ref = doc(db, collectionName, id);
        await updateDoc(ref, { [childArrayFieldName]: arrayRemove(removeValue), modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

        return true;
    } catch (error) {
        reportFirestoreWriteError('removeValueFromChildArray', `${collectionName}/${id}`, error);
    }

    return false;
};

// add an object to a child array within an existing document
export const addObjectToChildArray = async (collectionName, id, childArrayFieldName, childIdFieldName, newChildObject) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = addRecordToBottom(childIdFieldName, newChildObject, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, { [childArrayFieldName]: updatedList, modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('addObjectToChildArray', `${collectionName}/${id}`, error);
    }

    return false;
};

// add an object to a child array within an existing document and update additional fields
export const addObjectToChildArrayAndUpdateAdditionalFields = async (collectionName, id, childArrayFieldName, childIdFieldName, newChildObject, additionalUpdatedFields) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = addRecordToBottom(childIdFieldName, newChildObject, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, {
                ...additionalUpdatedFields,
                [childArrayFieldName]: updatedList,
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('addObjectToChildArrayAndUpdateAdditionalFields', `${collectionName}/${id}`, error);
    }

    return false;
};

// add an object to a child array within an existing document and refresh the array count
export const addObjectToChildArrayAndRefreshCount = async (collectionName, id, childArrayFieldName, childIdFieldName, newChildObject, childArrayCountFieldName) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = addRecordToBottom(childIdFieldName, newChildObject, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, {
                [childArrayFieldName]: updatedList,
                [childArrayCountFieldName]: updatedList.length,
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('addObjectToChildArrayAndRefreshCount', `${collectionName}/${id}`, error);
    }

    return false;
};

// update an object with the given childId in a child array within an existing document
export const updateObjectInChildArray = async (collectionName, id, childArrayFieldName, childIdFieldName, updatedChildObject) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = updateRecord(childIdFieldName, updatedChildObject, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, { [childArrayFieldName]: updatedList, modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('updateObjectInChildArray', `${collectionName}/${id}`, error);
    }

    return false;
};

// update an object with the given childId in a child array within an existing document and refresh the array count
export const updateObjectInChildArrayAndRefreshCount = async (collectionName, id, childArrayFieldName, childIdFieldName, updatedChildObject, childArrayCountFieldName) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = updateRecord(childIdFieldName, updatedChildObject, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, {
                [childArrayFieldName]: updatedList,
                [childArrayCountFieldName]: updatedList.length,
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('updateObjectInChildArrayAndRefreshCount', `${collectionName}/${id}`, error);
    }

    return false;
};

// update a field for an object with the given childId in a child array within an existing document
export const updateObjectFieldInChildArray = async (collectionName, id, childArrayFieldName, childIdFieldName, childIdFieldValue, updatedChildFieldName, updatedChildFieldValue) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = updateRecordField(childIdFieldName, childIdFieldValue, updatedChildFieldName, updatedChildFieldValue, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, {
                [childArrayFieldName]: updatedList,
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('updateObjectFieldInChildArray', `${collectionName}/${id}`, error);
    }

    return false;
};

// update fields for an object with the given childId in a child array within an existing document
export const updateObjectFieldsInChildArray = async (collectionName, id, childArrayFieldName, childIdFieldName, childIdFieldValue, updatedChildFields) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = updateRecordFields(childIdFieldName, childIdFieldValue, updatedChildFields, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, {
                [childArrayFieldName]: updatedList,
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('updateObjectFieldsInChildArray', `${collectionName}/${id}`, error);
    }

    return false;
};

// remove an object with the given childId from a child array within an existing document
export const removeObjectFromChildArray = async (collectionName, id, childArrayFieldName, childIdFieldName, childIdFieldValue) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = removeRecord(childIdFieldName, childIdFieldValue, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, {
                [childArrayFieldName]: updatedList,
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('removeObjectFromChildArray', `${collectionName}/${id}`, error);
    }

    return false;
};

// remove an object with the given childId from a child array within an existing document and refresh count
export const removeObjectFromChildArrayAndRefreshCount = async (collectionName, id, childArrayFieldName, childIdFieldName, childIdFieldValue, childArrayCountFieldName) => {
    try {
        const existingDocument = await getById(collectionName, id);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = removeRecord(childIdFieldName, childIdFieldValue, existingDocument[childArrayFieldName]);

            const ref = doc(db, collectionName, id);
            await updateDoc(ref, {
                [childArrayFieldName]: updatedList,
                [childArrayCountFieldName]: updatedList.length,
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            return true;
        }
    } catch (error) {
        reportFirestoreWriteError('removeObjectFromChildArrayAndRefreshCount', `${collectionName}/${id}`, error);
    }

    return false;
};

//#region SubCollection

export const subCollectionGetBySingleQuery = async (subCollectionName, fieldName, fieldValue, comparer) => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }

    return await getByQuery(query(collectionGroup(db, subCollectionName), where(fieldName, comparer, fieldValue)), subCollectionName);
};

export const subCollectionGetByDoubleQuery = async (subCollectionName, fieldName, fieldValue, comparer, fieldName2, fieldValue2, comparer2) => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }
    if ((comparer2 === '==' || comparer2 === 'array-contains') && isStringEmpty(fieldValue2)) {
        return [];
    }
    if ((comparer2 === 'in' || comparer2 === 'not-in' || comparer2 === 'array-contains-any') && isListEmpty(fieldValue2)) {
        return [];
    }

    return await getByQuery(query(collectionGroup(db, subCollectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2)), subCollectionName);
};

export const subCollectionGetByDoubleQueryPaginated = async (subCollectionName, fieldName, fieldValue, comparer, fieldName2, fieldValue2, comparer2, pageSize = 25, firstVisible, lastVisible, orderByField1 = 'createdAt', orderByField2 = 'createdBy') => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }
    if ((comparer2 === '==' || comparer2 === 'array-contains') && isStringEmpty(fieldValue2)) {
        return [];
    }
    if ((comparer2 === 'in' || comparer2 === 'not-in' || comparer2 === 'array-contains-any') && isListEmpty(fieldValue2)) {
        return [];
    }

    let q = query(collectionGroup(db, subCollectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2), orderBy(orderByField1, 'asc'), orderBy(orderByField2, 'asc'), limit(pageSize));
    if (isStringNotEmpty(firstVisible)) {
        q = query(collectionGroup(db, subCollectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2), orderBy(orderByField1, 'asc'), orderBy(orderByField2, 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField1], firstVisible[orderByField2]));
    } else if (isStringNotEmpty(lastVisible)) {
        q = query(collectionGroup(db, subCollectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2), orderBy(orderByField1, 'asc'), orderBy(orderByField2, 'asc'), limit(pageSize), startAfter(lastVisible[orderByField1], lastVisible[orderByField2]));
    }

    return await getByQuery(q, subCollectionName);
};

export const subCollectionGetByTripleQuery = async (subCollectionName, fieldName, fieldValue, comparer, fieldName2, fieldValue2, comparer2, fieldName3, fieldValue3, comparer3) => {
    if ((comparer === '==' || comparer === 'array-contains') && isStringEmpty(fieldValue)) {
        return [];
    }
    if ((comparer === 'in' || comparer === 'not-in' || comparer === 'array-contains-any') && isListEmpty(fieldValue)) {
        return [];
    }
    if ((comparer2 === '==' || comparer2 === 'array-contains') && isStringEmpty(fieldValue2)) {
        return [];
    }
    if ((comparer2 === 'in' || comparer2 === 'not-in' || comparer2 === 'array-contains-any') && isListEmpty(fieldValue2)) {
        return [];
    }
    if ((comparer3 === '==' || comparer3 === 'array-contains') && isStringEmpty(fieldValue3)) {
        return [];
    }
    if ((comparer3 === 'in' || comparer3 === 'not-in' || comparer3 === 'array-contains-any') && isListEmpty(fieldValue3)) {
        return [];
    }

    return await getByQuery(query(collectionGroup(db, subCollectionName), where(fieldName, comparer, fieldValue), where(fieldName2, comparer2, fieldValue2), where(fieldName3, comparer3, fieldValue3)), subCollectionName);
};

export const subCollectionGetAll = async (subCollectionName) => {
    const subCol = collectionGroup(db, subCollectionName);
    const snapshot = await getDocs(subCol);
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id, // append document id to each document
            ...doc.data()
        };
    });
};

export const subCollectionGetAllPaginated = async (subCollectionName, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
    let q = query(collectionGroup(db, subCollectionName), orderBy(orderByField, 'asc'), limit(pageSize));
    if (isStringNotEmpty(firstVisible)) {
        q = query(collectionGroup(db, subCollectionName), orderBy(orderByField, 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField]));
    } else if (isStringNotEmpty(lastVisible)) {
        q = query(collectionGroup(db, subCollectionName), orderBy(orderByField, 'asc'), limit(pageSize), startAfter(lastVisible[orderByField]));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id, // append document id to each document
            ...doc.data()
        };
    });
};

export const subCollectionGetByParentIdAndSubId = async (collectionName, parentId, subCollectionName, subId) => {
    const ref = doc(collection(db, collectionName), parentId, subCollectionName, subId);
    return await getReference(ref);
};

export const subCollectionGetByParentId = async (collectionName, parentId, subCollectionName) => {
    const subCol = collection(db, `${collectionName}/${parentId}/${subCollectionName}`);
    const snapshot = await getDocs(subCol);
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id, // append document id to each document
            ...doc.data()
        };
    });
};

// add an object to a subcollection within an existing document
export const subCollectionCreate = async (collectionName, id, subCollectionName, subId, data) => {
    try {
        data.createdAt = moment().utc().toDate();
        data.createdBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.modifiedAt = moment().utc().toDate();
        data.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
        data.isDeleted = false;

        const ref = doc(collection(db, collectionName), id, subCollectionName, subId);

        await runTransaction(db, async (transaction) => {
            const docSnap = await transaction.get(ref);
            // if the doc doesn't exist then we are good to create it
            if (!docSnap.exists()) {
                transaction.set(ref, data);
            } else {
                const docData = docSnap.data();

                if (docData === null || docData === undefined) {
                    transaction.set(ref, data);
                }
            }
        });

        return await getReference(ref);
    } catch (error) {
        reportFirestoreWriteError('subCollectionCreate', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return null;
};

// update an object with the given childId in a subcollection within an existing document
export const subCollectionUpdate = async (collectionName, id, subCollectionName, subId, data) => {
    try {
        data.modifiedAt = moment().utc().toDate();
        data.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';

        const ref = doc(collection(db, collectionName), id, subCollectionName, subId);
        await updateDoc(ref, data);

        return true;
    } catch (error) {
        reportFirestoreWriteError('subCollectionUpdate', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// update an object with the given childId in a subcollection within an existing document but dont allow overwriting of specific field
export const subCollectionUpdateWithoutOverwrite = async (collectionName, id, subCollectionName, subId, data, noOverwriteFieldName) => {
    try {
        data.modifiedAt = moment().utc().toDate();
        data.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';

        const ref = doc(collection(db, collectionName), id, subCollectionName, subId);

        await runTransaction(db, async (transaction) => {
            const docSnap = await transaction.get(ref);
            // if the doc doesn't exist then we are good to create it
            if (docSnap.exists()) {
                const docData = docSnap.data();
                if (docData[noOverwriteFieldName] === null || docData[noOverwriteFieldName] === undefined) {
                    transaction.set(ref, data, { merge: true });
                }
            }
        });

        return await getReference(ref);
    } catch (error) {
        reportFirestoreWriteError('subCollectionUpdateWithoutOverwrite', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return null;
};

// update a field for an object with the given childId in a subcollection within an existing document
export const subCollectionUpdateField = async (collectionName, id, subCollectionName, subId, fieldName, newValue) => {
    try {
        const ref = doc(collection(db, collectionName), id, subCollectionName, subId);
        await updateDoc(ref, { [fieldName]: newValue, modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

        return true;
    } catch (error) {
        reportFirestoreWriteError('subCollectionUpdateField', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

export const subCollectionUpdateFields = async (collectionName, id, subCollectionName, subId, updatedFields) => {
    try {
        const ref = doc(collection(db, collectionName), id, subCollectionName, subId);
        await updateDoc(ref, { ...updatedFields, modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

        return true;
    } catch (error) {
        reportFirestoreWriteError('subCollectionUpdateFields', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// remove an object with the given childId from a subcollection within an existing document
export const subCollectionRemove = async (collectionName, id, subCollectionName, subId) => {
    try {
        const ref = doc(collection(db, collectionName), id, subCollectionName, subId);
        await deleteDoc(ref);

        return true;
    } catch (error) {
        reportFirestoreWriteError('subCollectionRemove', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// add a value to a child array within an existing subcollection object
export const subCollectionAddValueToChildArray = async (collectionName, id, subCollectionName, subId, childArrayFieldName, newValue) => {
    try {
        const ref = doc(collection(db, collectionName), id, subCollectionName, subId);
        await updateDoc(ref, { [childArrayFieldName]: arrayUnion(newValue), modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

        return true;
    } catch (error) {
        reportFirestoreWriteError('subCollectionAddValueToChildArray', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// remove a value from a child array within an existing subcollection object
export const subCollectionRemoveValueFromChildArray = async (collectionName, id, subCollectionName, subId, childArrayFieldName, removeValue) => {
    try {
        const ref = doc(collection(db, collectionName), id, subCollectionName, subId);
        await updateDoc(ref, { [childArrayFieldName]: arrayRemove(removeValue), modifiedAt: moment().utc().toDate(), modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM' });

        return true;
    } catch (error) {
        reportFirestoreWriteError('subCollectionRemoveValueFromChildArray', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// add an object to a child array within an existing subcollection object
export const subCollectionAddObjectToChildArray = async (collectionName, id, subCollectionName, subId, childArrayFieldName, childIdFieldName, newChildObject) => {
    try {
        const existingDocument = await subCollectionGetByParentIdAndSubId(collectionName, id, subCollectionName, subId);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = addRecordToBottom(childIdFieldName, newChildObject, existingDocument[childArrayFieldName]);
            return await subCollectionUpdateField(collectionName, id, subCollectionName, subId, childArrayFieldName, updatedList);
        }
    } catch (error) {
        reportFirestoreWriteError('subCollectionAddObjectToChildArray', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};







// add an object to a child array within an existing subcollection object and refresh the array count
export const subCollectionAddObjectToChildArrayAndRefreshCount = async (collectionName, id, subCollectionName, subId, childArrayFieldName, childIdFieldName, newChildObject, childArrayCountFieldName) => {
    try {
        const existingDocument = await subCollectionGetByParentIdAndSubId(collectionName, id, subCollectionName, subId);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = addRecordToBottom(childIdFieldName, newChildObject, existingDocument[childArrayFieldName]);
            return await subCollectionUpdateFields(collectionName, id, subCollectionName, subId, {
                [childArrayFieldName]: updatedList,
                [childArrayCountFieldName]: updatedList.length
            });
        }
    } catch (error) {
        reportFirestoreWriteError('subCollectionAddObjectToChildArrayAndRefreshCount', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// update an object with the given childId in a child array within an existing subcollection object
export const subCollectionUpdateObjectInChildArray = async (collectionName, id, subCollectionName, subId, childArrayFieldName, childIdFieldName, updatedChildObject) => {
    try {
        const existingDocument = await subCollectionGetByParentIdAndSubId(collectionName, id, subCollectionName, subId);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = updateRecord(childIdFieldName, updatedChildObject, existingDocument[childArrayFieldName]);
            return await subCollectionUpdateField(collectionName, id, subCollectionName, subId, childArrayFieldName, updatedList);
        }
    } catch (error) {
        reportFirestoreWriteError('subCollectionUpdateObjectInChildArray', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// update an object with the given childId in a child array within an existing subcollection object and refresh the array count
export const subCollectionUpdateObjectInChildArrayAndRefreshCount = async (collectionName, id, subCollectionName, subId, childArrayFieldName, childIdFieldName, updatedChildObject, childArrayCountFieldName) => {
    try {
        const existingDocument = await subCollectionGetByParentIdAndSubId(collectionName, id, subCollectionName, subId);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = updateRecord(childIdFieldName, updatedChildObject, existingDocument[childArrayFieldName]);
            return await subCollectionUpdateFields(collectionName, id, subCollectionName, subId, {
                [childArrayFieldName]: updatedList,
                [childArrayCountFieldName]: updatedList.length
            });
        }
    } catch (error) {
        reportFirestoreWriteError('subCollectionUpdateObjectInChildArrayAndRefreshCount', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// update a field for an object with the given childId in a child array within an existing subcollection object
export const subCollectionUpdateObjectFieldInChildArray = async (collectionName, id, subCollectionName, subId, childArrayFieldName, childIdFieldName, childIdFieldValue, updatedChildFieldName, updatedChildFieldValue) => {
    try {
        const existingDocument = await subCollectionGetByParentIdAndSubId(collectionName, id, subCollectionName, subId);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = updateRecordField(childIdFieldName, childIdFieldValue, updatedChildFieldName, updatedChildFieldValue, existingDocument[childArrayFieldName]);
            return await subCollectionUpdateField(collectionName, id, subCollectionName, subId, childArrayFieldName, updatedList);
        }
    } catch (error) {
        reportFirestoreWriteError('subCollectionUpdateObjectFieldInChildArray', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// update fields for an object with the given childId in a child array within an existing subcollection object
export const subCollectionUpdateObjectFieldsInChildArray = async (collectionName, id, subCollectionName, subId, childArrayFieldName, childIdFieldName, childIdFieldValue, updatedChildFields) => {
    try {
        const existingDocument = await subCollectionGetByParentIdAndSubId(collectionName, id, subCollectionName, subId);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = updateRecordFields(childIdFieldName, childIdFieldValue, updatedChildFields, existingDocument[childArrayFieldName]);
            return await subCollectionUpdateField(collectionName, id, subCollectionName, subId, childArrayFieldName, updatedList);
        }
    } catch (error) {
        reportFirestoreWriteError('subCollectionUpdateObjectFieldsInChildArray', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// remove an object with the given childId from a child array within an existing subcollection object
export const subCollectionRemoveObjectFromChildArray = async (collectionName, id, subCollectionName, subId, childArrayFieldName, childIdFieldName, childIdFieldValue) => {
    try {
        const existingDocument = await subCollectionGetByParentIdAndSubId(collectionName, id, subCollectionName, subId);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = removeRecord(childIdFieldName, childIdFieldValue, existingDocument[childArrayFieldName]);
            return await subCollectionUpdateField(collectionName, id, subCollectionName, subId, childArrayFieldName, updatedList);
        }
    } catch (error) {
        reportFirestoreWriteError('subCollectionRemoveObjectFromChildArray', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

// remove an object with the given childId from a child array within an existing subcollection object and refresh count
export const subCollectionRemoveObjectFromChildArrayAndRefreshCount = async (collectionName, id, subCollectionName, subId, childArrayFieldName, childIdFieldName, childIdFieldValue, childArrayCountFieldName) => {
    try {
        const existingDocument = await subCollectionGetByParentIdAndSubId(collectionName, id, subCollectionName, subId);
        if (isObjectNotEmpty(existingDocument)) {
            let updatedList = removeRecord(childIdFieldName, childIdFieldValue, existingDocument[childArrayFieldName]);
            return await subCollectionUpdateFields(collectionName, id, subCollectionName, subId, {
                [childArrayFieldName]: updatedList,
                [childArrayCountFieldName]: updatedList.length
            });
        }
    } catch (error) {
        reportFirestoreWriteError('subCollectionRemoveObjectFromChildArrayAndRefreshCount', `${collectionName}/${id}/${subCollectionName}/${subId}`, error);
    }

    return false;
};

//#endregion
//#region utils

export const updateObject = (oldObject, updatedValues) => {
    return {
        ...oldObject,
        ...updatedValues
    };
};

export const addObjectToTopOfArray = (oldObjects, newObject) => {
    return [newObject, ...oldObjects];
};

export const addObjectToBottomOfArray = (oldObjects, newObject) => {
    return [...oldObjects, newObject];
};

export const addRecordToTop = (idFieldName, newRecord, oldList) => {
    // check to see if record already exists in the old list, update if so, insert if not
    let existingList = isListNotEmpty(oldList) ? [...oldList] : [];
    const index = existingList.findIndex(obj => obj[idFieldName] === newRecord[idFieldName]);
    let updatedRecords = [];
    // only update if the record exists in the list
    if (index !== -1) {
        updatedRecords = [
            ...existingList.slice(0, index), // everything before current obj
            newRecord,
            ...existingList.slice(index + 1), // everything after current obj
        ];
    } else {
        updatedRecords = addObjectToTopOfArray(existingList, newRecord);
    }

    return updatedRecords;
};

export const addRecordToBottom = (idFieldName, newRecord, oldList) => {
    // check to see if record already exists in the old list, update if so, insert if not
    let existingList = isListNotEmpty(oldList) ? [...oldList] : [];
    const index = existingList.findIndex(obj => obj[idFieldName] === newRecord[idFieldName]);
    let updatedRecords = [];
    // only update if the record exists in the list
    if (index !== -1) {
        updatedRecords = [
            ...existingList.slice(0, index), // everything before current obj
            newRecord,
            ...existingList.slice(index + 1), // everything after current obj
        ];
    } else {
        updatedRecords = addObjectToBottomOfArray(existingList, newRecord);
    }

    return updatedRecords;
};

export const updateRecord = (idFieldName, updatedRecord, oldList) => {
    // check to see if record already exists in the old list, update if so, insert if not
    let existingList = isListNotEmpty(oldList) ? [...oldList] : [];
    const index = existingList.findIndex(obj => obj[idFieldName] === updatedRecord[idFieldName]);
    let updatedRecords = [];
    // only update if the record exists in the list
    if (index !== -1) {
        updatedRecords = [
            ...existingList.slice(0, index), // everything before current obj
            updatedRecord,
            ...existingList.slice(index + 1), // everything after current obj
        ];
    } else {
        updatedRecords = addObjectToBottomOfArray(existingList, updatedRecord);
    }

    return updatedRecords;
};

export const updateRecordField = (idFieldName, idFieldValue, updatedFieldName, updatedFieldValue, oldList) => {
    if (isListNotEmpty(oldList)) {
        let existingList = [...oldList];
        const index = existingList.findIndex(obj => obj[idFieldName] === idFieldValue);
        let updatedRecords = [];
        if (index !== -1) {
            const existingRecord = existingList[index];
            // only update if the record exists in the list
            if (isObjectNotEmpty(existingRecord)) {
                let updatedRecord = { ...existingRecord, [updatedFieldName]: updatedFieldValue };
                updatedRecords = [
                    ...existingList.slice(0, index), // everything before current obj
                    updatedRecord,
                    ...existingList.slice(index + 1), // everything after current obj
                ];

                return updatedRecords;
            }
        }
    }

    return oldList;
};

export const updateRecordFields = (idFieldName, idFieldValue, updatedFields, oldList) => {
    if (isListNotEmpty(oldList)) {
        let existingList = [...oldList];
        const index = existingList.findIndex(obj => obj[idFieldName] === idFieldValue);
        let updatedRecords = [];
        if (index !== -1) {
            const existingRecord = existingList[index];
            // only update if the record exists in the list
            if (isObjectNotEmpty(existingRecord)) {
                let updatedRecord = { ...existingRecord, ...updatedFields };
                updatedRecords = [
                    ...existingList.slice(0, index), // everything before current obj
                    updatedRecord,
                    ...existingList.slice(index + 1), // everything after current obj
                ];

                return updatedRecords;
            }
        }
    }

    return oldList;
};

export const removeRecord = (idFieldName, idToRemove, oldList) => {
    if (isListNotEmpty(oldList)) {
        let existingList = [...oldList];
        const index = existingList.findIndex(obj => obj[idFieldName] === idToRemove);
        // only update if the record exists in the list
        if (index !== -1) {
            // returns the deleted items
            existingList.splice(index, 1);
        }

        return existingList;
    }

    return oldList;
};

//#endregion